/**
 * One partner's payout plan: the programme's, or one of its own; and staff's
 * hold, which stops any payout being prepared, asked for or approved — with a
 * reason, because a hold nobody can explain is a payment nobody can defend.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  DetailField,
  DetailGrid,
  Input,
  LoadingState,
  Switch,
  formatDate,
  formatMoney,
  toast,
} from '@connect/web-shared';

import { partnerKeys, partnersApi, type PartnerPayoutPlan } from './api';
import { PlanEditor, planFrom, type PayoutPlan } from './plan-editor';
import { Failed, errorText } from './shared';

export function PartnerPayoutPlanSection({ partnerId }: { partnerId: string }) {
  const plan = useQuery({ queryKey: partnerKeys.plan(partnerId), queryFn: () => partnersApi.payoutPlan(partnerId) });
  if (plan.isPending) return <LoadingState />;
  if (plan.isError) return <Failed error={plan.error} onRetry={() => void plan.refetch()} />;
  return <PlanForm partnerId={partnerId} current={plan.data} />;
}

function PlanForm({ partnerId, current }: { partnerId: string; current: PartnerPayoutPlan }) {
  const queryClient = useQueryClient();
  const [own, setOwn] = useState(current.own);
  const [plan, setPlan] = useState<PayoutPlan>(planFrom(current));
  const [hold, setHold] = useState(current.on_hold);
  const [reason, setReason] = useState(current.hold_reason ?? '');
  const save = useMutation({
    mutationFn: () =>
      partnersApi.setPayoutPlan(partnerId, {
        plan: own ? plan : null,
        payouts_on_hold: hold,
        payout_hold_reason: hold ? reason : null,
      }),
    onSuccess: async () => {
      toast.success('Payout plan saved');
      await queryClient.invalidateQueries({ queryKey: partnerKeys.plan(partnerId) });
    },
  });
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title="How this partner is paid" description={current.describe} />
        <CardBody className="space-y-4">
          <DetailGrid>
            <DetailField label="Next scheduled payout">
              {current.next_payout ? formatDate(current.next_payout) : 'Not on a schedule'}
            </DetailField>
            <DetailField label="Least a payout may be">{formatMoney(current.minimum_payout)}</DetailField>
            <DetailField label="Settlement period">{current.settlement_days} days</DetailField>
            <DetailField label="Plan">{current.own ? 'Its own' : `The programme's: ${current.programme_plan}`}</DetailField>
          </DetailGrid>
          {current.on_hold ? (
            <Alert tone="warning" title="Payouts are on hold">
              {current.hold_reason}
            </Alert>
          ) : null}
        </CardBody>
      </Card>
      <Can permission="partners.payout.manage">
        <Card>
          <CardHeader title="Change it" />
          <CardBody className="space-y-4">
            {save.isError ? <Alert tone="danger">{errorText(save.error)}</Alert> : null}
            <Switch label="Give this partner a plan of its own" checked={own} onChange={(e) => setOwn(e.target.checked)} />
            {own ? <PlanEditor value={plan} onChange={setPlan} /> : null}
            <Switch label="Hold this partner's payouts" checked={hold} onChange={(e) => setHold(e.target.checked)} />
            {hold ? <Input label="Why" value={reason} onChange={(e) => setReason(e.target.value)} /> : null}
            <Button loading={save.isPending} disabled={hold && reason.trim().length < 3} onClick={() => save.mutate()}>
              Save
            </Button>
          </CardBody>
        </Card>
      </Can>
    </div>
  );
}
